/**
 * Quick capture with a type choice — SPEC-2 §2, voice first since Spec 022
 * (round-2 screen 7).
 *
 * `CaptureSheet` is the form in its sheet; «●Записать» in the bottom bar opens it
 * directly (Spec 020 D5). `CaptureBar` is the inbox's «Записать мысль…» row that
 * opens the same sheet. One sheet either way — never a sheet with a button that
 * opens another (PR #13 review, blocker 3).
 *
 * Voice: the recording goes to the person's own «Сегодня» note (private, the
 * server's Whisper transcribes any audio there), and the transcript lands in
 * the field. The person reads it, picks what it is and saves — a recording
 * never becomes an idea or a task by itself, and the audio stays in «Сегодня».
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, FileText, Lightbulb, ListTodo, Mic, Paperclip, Square } from "lucide-react";

import { api, upload } from "@/api/client";
import { Ideas, Methods, Notes, Projects, Tasks } from "@/api/resources";
import type { AttachmentOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { SelectField, TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { useToast } from "@/components/ui/Toast";
import { Chip } from "@/kit";
import { cn } from "@/lib/cn";
import { INBOX_PROJECT_ID, MAX_ATTACHMENT_BYTES } from "@/lib/limits";

type CaptureKind = "idea" | "method" | "material" | "task";

const KINDS: { value: CaptureKind; label: string; icon: typeof Lightbulb; hint: string }[] = [
  {
    value: "idea",
    label: "Идея",
    icon: Lightbulb,
    hint: "Мысль, наблюдение, «надо подумать про». Партнёр решит, что с ней делать.",
  },
  {
    value: "task",
    label: "Задача",
    icon: ListTodo,
    hint: "Конкретное дело — в выбранный проект.",
  },
  {
    value: "material",
    label: "Заметка",
    icon: FileText,
    hint: "Ссылка, цитата, файл — общая заметка, видна вам обоим.",
  },
  {
    value: "method",
    label: "Метод",
    icon: BookOpen,
    hint: "Повторяемый порядок действий. Сохранится черновиком — доказать его может только применение.",
  },
];

/**
 * The default is `idea`, and it is an invariant rather than a preference.
 *
 * The cost of the two mistakes is not symmetric: an idea filed as a method
 * poisons the one part of the workspace that is supposed to be trustworthy,
 * while a method filed as an idea just waits in the pool until someone
 * promotes it. So the type chips start on идея and the person moves off it
 * deliberately — never the other way round.
 */
const DEFAULT_KIND: CaptureKind = "idea";

// PC audit 28.09, point 12: a task goes to a project chosen here, the last one by
// default — an agent does not work on a task in «Входящие» (Spec 024).
const LAST_PROJECT = "prosto.capture.project";
function lastProject(): string | null {
  try { return window.localStorage.getItem(LAST_PROJECT); } catch { return null; }
}
function rememberProject(id: string): void {
  try { window.localStorage.setItem(LAST_PROJECT, id); } catch { /* private mode: nothing to remember */ }
}

type Voice = "idle" | "recording" | "transcribing";
type Transcribed = AttachmentOut & { transcript?: string | null; transcription_status?: string };

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Upload to «Сегодня», then wait for Whisper — up to three minutes. */
async function transcribe(file: File, alive: () => boolean): Promise<string | null> {
  const time = new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const today = await Notes.appendToday(`Голосовая запись из «Записать», ${time}`);
  const form = new FormData();
  form.set("parent_type", "note");
  form.set("parent_id", today.id);
  form.set("file", file);
  const saved = await upload<Transcribed>("/api/v1/attachments", form, undefined, crypto.randomUUID());
  for (let i = 0; i < 60 && alive(); i++) {
    await sleep(3000);
    const row = await api<Transcribed>(`/api/v1/attachments/${saved.id}/transcript`);
    if (row.transcription_status === "done") return row.transcript?.trim() || null;
    if (row.transcription_status === "failed") return null;
  }
  return null;
}

export function CaptureSheet({
  open,
  onClose,
  onCaptured,
  title = "Записать",
}: {
  open: boolean;
  onClose: () => void;
  onCaptured?: () => void;
  title?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<CaptureKind>(DEFAULT_KIND);
  const [projectId, setProjectId] = useState<string | null>(null);
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list(), enabled: open && kind === "task" });
  const projectOptions = (projects.data ?? [])
    .filter((p) => !p.archived_at)
    .sort((a, b) => Number(a.id === INBOX_PROJECT_ID) - Number(b.id === INBOX_PROJECT_ID))
    .map((p) => ({ value: p.id, label: p.id === INBOX_PROJECT_ID ? "Входящие — разложу потом" : p.title }));
  const remembered = lastProject();
  const target = projectId
    ?? (remembered && projectOptions.some((o) => o.value === remembered) ? remembered : null)
    ?? projectOptions.find((o) => o.value !== INBOX_PROJECT_ID)?.value
    ?? INBOX_PROJECT_ID;
  const [text, setText] = useState("");
  const [voice, setVoice] = useState<Voice>("idle");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const ticker = useRef<ReturnType<typeof setInterval>>();
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const stopStream = () => {
    clearInterval(ticker.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };
  useEffect(() => () => {
    if (recorder.current?.state === "recording") recorder.current.stop();
    stopStream();
  }, []);

  const reset = () => {
    if (recorder.current?.state === "recording") recorder.current.stop();
    stopStream();
    setText("");
    setKind(DEFAULT_KIND);
    setVoice("idle");
  };

  const record = async () => {
    if (voice === "recording") {
      recorder.current?.stop();
      return;
    }
    if (voice !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Запись здесь недоступна. Напиши текстом или отправь голосовое боту.");
      return;
    }
    try {
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = acquired;
      const mime = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"].find((t) => MediaRecorder.isTypeSupported(t));
      const next = new MediaRecorder(acquired, mime ? { mimeType: mime } : undefined);
      recorder.current = next;
      const chunks: Blob[] = [];
      let total = 0;
      next.ondataavailable = (event) => {
        chunks.push(event.data);
        total += event.data.size;
        if (total >= MAX_ATTACHMENT_BYTES && next.state === "recording") next.stop();
      };
      next.onstop = () => {
        stopStream();
        if (!openRef.current) return setVoice("idle");
        const type = next.mimeType || "audio/webm";
        const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File(chunks, `voice-${Date.now()}.${extension}`, { type });
        if (!file.size) return setVoice("idle");
        setVoice("transcribing");
        transcribe(file, () => openRef.current)
          .then((words) => {
            if (!openRef.current) return;
            if (words) setText((current) => (current.trim() ? `${current.trim()}\n${words}` : words));
            else toast.error("Не удалось расшифровать. Запись сохранена в заметке «Сегодня».");
          })
          .catch(() => toast.error("Запись не отправилась. Проверь связь и попробуй ещё раз."))
          .finally(() => setVoice("idle"));
      };
      next.start(1000);
      setSeconds(0);
      setVoice("recording");
      ticker.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= 300 && next.state === "recording") next.stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      stopStream();
      toast.error("Нет доступа к микрофону. Разреши его или напиши текстом.");
    }
  };

  const capture = useMutation({
    mutationFn: async ({ value, as }: { value: string; as: CaptureKind }) => {
      const firstLine = value.split("\n", 1)[0].slice(0, 240);
      switch (as) {
        case "method":
          // Always a draft with only the words that were typed. Preconditions,
          // checks and pitfalls stay empty rather than guessed — a guessed
          // precondition reads exactly like a verified one to the next reader.
          return Methods.create({ title: firstLine, first_version: { body_md: value } });
        case "material":
          return Notes.create({ title: firstLine, body_md: value, tags: ["material"], is_shared: true });
        case "task":
          rememberProject(target);
          return Tasks.create({ project_id: target, title: firstLine, description_md: value });
        case "idea":
        default:
          return Ideas.create({ original_text: value });
      }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["ideas"] });
      qc.invalidateQueries({ queryKey: ["methods"] });
      qc.invalidateQueries({ queryKey: ["notes"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      toast.success(
        variables.as === "method"
          ? "Черновик метода сохранён"
          : variables.as === "material"
            ? "Заметка сохранена"
            : variables.as === "task"
              ? "Задача создана"
              : "Идея в пуле",
      );
      reset();
      onClose();
      onCaptured?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const active = KINDS.find((k) => k.value === kind)!;
  const busy = capture.isPending || voice !== "idle";
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <Sheet
      open={open}
      onClose={() => {
        if (capture.isPending) return;
        reset();
        onClose();
      }}
      title={title}
      footer={
        <Button
          variant="strong"
          block
          disabled={voice !== "idle" || !text.trim()}
          loading={capture.isPending}
          loadingText="Сохраняем…"
          onClick={() => {
            const value = text.trim();
            if (value) capture.mutate({ value, as: kind });
          }}
        >
          Сохранить
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {KINDS.map(({ value, label, icon: Icon }) => (
            <Chip key={value} on={kind === value} onClick={() => { if (!capture.isPending) setKind(value); }}>
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </Chip>
          ))}
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => void record()}
            disabled={voice === "transcribing" || capture.isPending}
            aria-label={voice === "recording" ? "Закончить запись" : "Записать голосом"}
            className={cn(
              "flex h-[72px] w-[72px] items-center justify-center rounded-full press-surface disabled:opacity-60",
              voice === "recording" ? "bg-p-needs text-p-surface" : "bg-p-text text-p-bg",
            )}
          >
            {voice === "recording" ? <Square className="h-7 w-7" aria-hidden="true" /> : <Mic className="h-8 w-8" aria-hidden="true" />}
          </button>
          <p className="text-center text-meta text-p-text-muted" aria-live="polite">
            {voice === "recording" ? <>Идёт запись · {clock}. Нажми, чтобы закончить.</>
              : voice === "transcribing" ? "Расшифровываем… обычно до минуты."
              : <>Нажми и говори — расшифруем, а ты проверишь.<br />Или напиши текстом:</>}
          </p>
        </div>

        <TextArea
          label="Что записать"
          disabled={busy}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Что записать?"
          rows={4}
        />
        {kind === "task" && (
          <SelectField label="Проект" value={target} onChange={(v) => setProjectId(v || null)} options={projectOptions}
            disabled={busy || projects.isLoading} placeholder={projects.isLoading ? "Загружаем проекты…" : "Входящие"} />
        )}
        <p className="text-meta text-p-text-muted">{active.hint}</p>
      </div>
    </Sheet>
  );
}

export function CaptureBar({ onCaptured }: { onCaptured?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="press-surface flex min-h-touch w-full items-center gap-2 rounded-card border border-p-border bg-p-surface px-4 text-left"
      >
        <span className="flex-1 text-cell text-p-text-muted">Записать мысль…</span>
        <Paperclip className="h-[18px] w-[18px] shrink-0 text-p-text-muted" aria-hidden="true" />
      </button>
      <CaptureSheet open={open} onClose={() => setOpen(false)} onCaptured={onCaptured} />
    </>
  );
}
